import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Live2D } from 'starry-sky-ui';
import { sendMessageStream } from '@/service/live2d';
import { useMascot } from './mascot-context';
import { detectAndExecute } from './intent-recognition';
import './index.scss';

const STORAGE_POS_KEY = 'floating-mascot-pos';
const DEFAULT_POS = { x: window.innerWidth - 420, y: window.innerHeight - 490 };
const WINDOW_WIDTH = 400; // 窗口宽度，用于 right offset

/** 解析 SSE 流，逐行读取 data: JSON 并提取 content 字段 */
async function readSSEBody(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  signal?: AbortSignal,
): Promise<string> {
  const decoder = new TextDecoder();
  let buffer = '';
  let fullReply = '';
  while (true) {
    if (signal?.aborted) break;
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || !trimmed.startsWith('data: ')) continue;
      try {
        const chunk = JSON.parse(trimmed.slice(6).trim());
        if (chunk.type === 'content' && chunk.content) fullReply += chunk.content;
      } catch { /* skip */ }
    }
  }
  return fullReply;
}

/** 看板娘对话回调（支持工具调用） */
const handleReply = async (
  input: string,
  history: { role: string; text: string }[],
): Promise<string> => {
  // ---- 意图识别 + 工具调用 ----
  const intent = await detectAndExecute(input);

  // ---- 构建消息列表 ----
  const systemContent = `你是一个名叫「蒂亚」的网页看板娘助手，性格温柔友善、略带俏皮。

## 核心设定
- 你是网站上的看板娘精灵，负责欢迎访客和简单互动
- 你喜欢和人聊天，态度友好自然
- 你有一些小个性，但不会过于夸张

## 语言风格
- 说话自然简洁，可以嘴甜撒娇，但不要太过
- 偶尔可以用语气词（呀、呢、哦、~）和颜文字
- 回复控制在 200 字以内
- 不要称呼对方为主人，正常用"你"就好

## 行为规则
- 看到代码相关问题可以简单回应，不需要假装不懂
- 不要输出格式化内容（代码块、Markdown）
- 保持闲聊风格，简短自然就好

## 工具能力
当用户询问推荐文章时，你可以使用"文章推荐工具"获取精选文章数据。
工具返回的数据格式为 Markdown 链接列表，请直接自然地展示给用户，
可以说"给你推荐几篇精选文章～"之类的话开头。`;

  const messages: { role: 'system' | 'user' | 'assistant'; content: string; id?: string }[] = [
    { role: 'system', content: systemContent, id: 'system' },
    ...history.map((t) => ({
      role: t.role === 'user' ? 'user' as const : 'system' as const,
      content: t.text,
    })),
  ];

  // ---- 如果有工具结果，注入到 user 消息之前 ----
  if (intent.type !== 'noNeed' && intent.data) {
    messages.push({
      role: 'system',
      content: `【工具调用结果 - 精选文章推荐】\n以下是根据用户请求获取的精选文章数据，请用自然的语气+跳转链接推荐给用户,注意博客文章的跳转链接为以下格式 当前域名/blog/article/:id：\n\n${intent.data}`,
    });
  }

  messages.push({ role: 'user' as const, content: input });

  const controller = new AbortController();
  const response = await sendMessageStream(messages, controller.signal);
  if (!response.ok || !response.body) {
    throw new Error(`HTTP ${response.status}`);
  }
  return readSSEBody(response.body.getReader(), controller.signal);
};

const BLOCKED_PATHS = ['/demo/live2d', '/ad-ui'];

const FloatingMascot: React.FC = () => {
  const { visible, toggle, close, open } = useMascot();
  const location = useLocation();
  const [shouldHide, setShouldHide] = useState(false);

  // ---- 问候语（同 Live2D 页面逻辑） ----
  const province = localStorage.getItem('province');

  // ---- 检测是否在阻塞页面（已有蒂亚的页面） ----
  const isBlockedPage = BLOCKED_PATHS.includes(location.pathname);

  // ---- 问候语：阻塞页面说告别，正常页面说欢迎 ----
  const greeting = isBlockedPage
    ? '这里已经有蒂亚了～我先走啦！'
    : `嗨～我是蒂亚，${province ? `来自${province}的朋友！` : ''}今天想聊点什么呀？`;

  // ---- 位置状态（拖拽吸附） ----
  const [pos, setPos] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_POS_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return DEFAULT_POS;
  });
  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, left: 0, top: 0 });
  const modelRef = useRef<HTMLDivElement>(null);

  /* ---- 拖拽逻辑 ---- */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.closest('.live2d-stage__input-area') ||
        target.closest('.live2d-stage__bubble--clickable') ||
        target.closest('.floating-mascot__close') ||
        target.closest('.live2d-stage__send')
      )
        return;
      dragging.current = true;
      dragStart.current = { x: e.clientX, y: e.clientY, left: pos.x, top: pos.y };
      e.preventDefault();
    },
    [pos],
  );

  useEffect(() => {
    if (isBlockedPage) {
      const timer = setTimeout(() => {
        setShouldHide(true);
        close();
      }, 2000);
      return () => {
        clearTimeout(timer);
        setShouldHide(false);
      };
    } else {
      setShouldHide(false);
      // 离开阻塞页面时重新打开
      if (!visible) {
        open();
      }
    }
  }, [isBlockedPage]);

  useEffect(() => {
    if (!visible) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      setPos({
        x: dragStart.current.left + (e.clientX - dragStart.current.x),
        y: dragStart.current.top + (e.clientY - dragStart.current.y),
      });
    };
    const handleMouseUp = () => {
      if (dragging.current) {
        dragging.current = false;
        setPos((p) => {
          localStorage.setItem(STORAGE_POS_KEY, JSON.stringify(p));
          return p;
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [visible]);

  // 保存位置
  useEffect(() => {
    localStorage.setItem(STORAGE_POS_KEY, JSON.stringify(pos));
  }, [pos]);

  // 阻塞页面隐藏整个组件
  if (shouldHide) return null;

  return (
    <>
      {/* ---- 触发按钮（类客服头像，始终可见） ---- */}
      <button
        className={`mascot-trigger ${visible ? 'mascot-trigger--active' : ''}`}
        onClick={toggle}
        title={visible ? '关闭看板娘' : '打开看板娘'}
      >
        <div className="mascot-trigger__avatar">
          <svg viewBox="0 0 48 48" fill="none" width="28" height="28">
            <circle cx="24" cy="16" r="8" fill="currentColor" opacity="0.9" />
            <path
              d="M8 44c0-8.8 7.2-16 16-16s16 7.2 16 16"
              fill="currentColor"
              opacity="0.9"
            />
          </svg>
        </div>
        <span className="mascot-trigger__label">蒂亚</span>
        {/* 呼吸光晕 */}
        <div className="mascot-trigger__ring" />
      </button>

      {/* ---- 看板娘窗口 ---- */}
      {visible && (
        <div
          ref={modelRef}
          className={`floating-mascot-window ${dragging.current ? 'floating-mascot-window--dragging' : ''}`}
          style={{ right: Math.max(10, window.innerWidth - (pos.x + WINDOW_WIDTH)), top: pos.y }}
          onMouseDown={handleMouseDown}
        >
          {/* 关闭 */}
          <button
            className="floating-mascot__close"
            onClick={(e) => { e.stopPropagation(); close(); }}
            title="关闭看板娘"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>

          <Live2D
            hideHistory
            name="蒂亚"
            greeting={greeting}
            onReply={handleReply}
            storageKey="live2d-ai-chats"
            autoMessages={['今天过得怎么样呀~', '好无聊呀，来陪我说说话嘛！']}
            autoInterval={600000}
          />
        </div>
      )}
    </>
  );
};

export default FloatingMascot;
